import React, { useCallback, useEffect, useRef, useState } from "react";
import { WEBGL } from "./utils/webGL.utils";
import Main3DManager from "./main.3d.manager";
import type { ArtworkLoadTelemetrySnapshot } from "./loaders/artwork/artwork.loader";
import "./styles/art.space.css";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import useEvents from "../../hooks/useEvents";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";

import AppData from "./app.data";
import { BuyArtworkModal } from "../modals/buy.artwork";
import { NewArtworkModal } from "../modals/new.artwork";
import { ToastContainer, toast } from "react-toastify";
import { ConfirmDeleteHexagonModal } from "../modals/confirm-delete-hexagon.modal";
import { Utils } from "./utils/utils";

import { GallerySetupModal, extractGalleryLabelIds } from "../modals/gallery.setup";
import { RenderSettingsModal } from "../modals/render.settings";
import { useAutoLogin } from "../../hooks/useAutoLogin";
import { ConfirmOpenHexagonModal } from "../modals/confirm-open-hexagon.modal";
import { EnquireModal } from "../modals/enquire.modal";
import { Artwork, ArtworkWrapper } from "@/types/artworksData";
import type { Prop } from "@/types/spaceData";
import { Slot } from "@/types/slotsData";
import { ListArtworkModalV2 } from "../modals/new-list.artwork";
import { resolveActiveMoodIndex } from "../../utils/moodVisibility";
import {
  canAdminEditPseudoMoMArtworkSlots,
  canUseCachedPublicGalleryReplay,
  collectGalleryArtworksFromWrappers,
  ensureGalleryBootstrapStep,
  getGalleryRouteId,
  isAuthRequiredGalleryMode,
  isGalleryKeyboardBlocked,
  isPseudoMoMSpace,
  isOfflineEligiblePublicGalleryRoute,
  resolveGalleryKeyboardAvailability,
  resolveArtworkSlotClickAction,
  resolveCanEditArtworkSlots,
  resolveClickedSlotIndex,
  resolveCanManageGalleryCollaborators,
  resolveCanManageGallerySettings,
  resolveCanMoveArtworks,
  resolveCanOpenGallerySetup,
  resolveGalleryCurrentUser,
  resolveGalleryIntroDurationSeconds,
  resolveGalleryViewerContext,
  resolveSavedMoodPersistence,
  shouldPausePseudoMoMModelRotation,
  shouldRunGalleryIntro,
  shouldSkipGalleryCurrentUserLookup,
  shouldShowOfflineUnavailableForBootstrapFailure,
  shouldShowGalleryStatsPanel,
  shouldUseCachedPublicGalleryArtworks,
} from "./gallery.bootstrap";
import {
  hydrateAutoPopulateArtworkWrappers,
  isInvalidPseudoMoMRemoveResponse,
  wrapperContainsArtwork,
} from "./gallery.artworkWrappers";
import { handleUnauthorizedSession } from "../../utils/authSession";
import { hasValidAccessToken } from "../../utils/authToken";
import { buildMovedArtworkPayloads } from "./utils/artworkPlacement.utils";
import { buildOfflineGalleryManifest } from "../../offline/offlineGalleryManifest";
import {
  getOfflineGalleryFailureMessage,
  getOfflineGalleryMetadata,
  prepareOfflineGallery,
  syncOfflineGalleryMetadataFromCache,
} from "../../offline/offlineGalleryManager";
import { OfflineModeState } from "../../offline/offline.types";

import "./styles/gallery.css";
import { Sounds } from "@/types/soundsData";
import {
  buildSkippedGalleryQuotaMessage,
  buildSkippedArtworkMessage,
  formatQuotaMb,
  getGalleryQuotaUsagePercent,
  normalizeGalleryStats,
} from "../../utils/galleryLoadQuota";
import type { GalleryStats } from "../../utils/galleryLoadQuota";
import { useGalleryChromeContext } from "../../contexts/GalleryChromeContext";
import {
  buildTelemetrySpaceContext,
  trackArtworkView,
  trackGalleryPerformance,
  trackGalleryView,
  type TelemetrySpaceContext,
} from "../../utils/telemetry";
import { resolveArtworkViewerMedia } from "./media.viewer.utils";
import {
  beginGalleryBenchmarkTeardown,
  completeGalleryBenchmarkTeardown,
  markGalleryBenchmark,
  recordGalleryBenchmarkError,
  recordGalleryBenchmarkRendererSnapshot,
  startGalleryBenchmark,
} from "../../performance/galleryBenchmark";
import {
  getRuntimeConfig,
  shouldSampleGalleryPerformance,
  type RenderQuality,
} from "../../utils/runtimeConfig";
import { isGalleryBackgroundMusicPlayable } from "../../utils/visitorPreferences";

const appData = new AppData()
const _3dUtil = new Utils();

const BASE_API = process.env.REACT_APP_BASE_API;
const ENABLE_3D_ARTWORK_DRAG_ROTATION = process.env.REACT_APP_ENABLE_3D_ARTWORK_DRAG_ROTATION === "true";

const getStoredRenderQuality = (): RenderQuality => {
  try {
    const quality = window.localStorage.getItem("renderQuality");
    return quality === "LQ" || quality === "HQ" ? quality : "MQ";
  } catch (_error) {
    return "MQ";
  }
};

interface GalleryComponentProps {
  feedbackModalOpen?: boolean;
  onOfflineModeChange?: (state: OfflineModeState) => void;
}

const ONLINE_OFFLINE_MODE_STATE: OfflineModeState = {
  status: "online",
  isOffline: false,
  cachedGallery: null,
  message: null,
};

export const GalleryComponent = React.memo(({
  feedbackModalOpen = false,
  onOfflineModeChange,
}: GalleryComponentProps = {}) => {
  const [artworksModal, setArtworksModal] = useState<boolean>(false);
  const [buyArtworkModal, setBuyArtworkModal] = useState<boolean>(false);
  const [createArtworkModal, setCreateArtworkModal] = useState<boolean>(false);
  const [gallerySetupModal, setGallerySetupModal] = useState<boolean>(false);
  const {
    setHasPlayableGalleryBackgroundMusic,
    setIsPseudoMoMGallery,
    visitorBackgroundMusicMuted,
  } = useGalleryChromeContext();
  const [enquireModal, setEnquireModal] = useState<boolean>(false);

  const [confirmDeleteHexagon, setConfirmDeleteHexagon] = useState<boolean>(false);
  const [spaceToRemove, setSpaceToRemove] = useState<number>(-1);

  const [confirmOpenHexagon, setConfirmOpenHexagon] = useState<boolean>(false);
  const [spaceToOpen, setSpaceToOpen] = useState<number>(-1);

  const [title, setTitle] = useState<string>('');
  const [artworkToEdit, setArtworkToEdit] = useState<Artwork>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string>();
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork>(null);

  const [searchParams] = useSearchParams();
  const currentRouteMode = searchParams.get("t");
  const [artworks, setArtworks] = useState<Artwork[]>([]);

  const [personal, setPersonal] = useState<boolean>(false);
  const [admin, setAdmin] = useState<boolean>(false);
  const [canManageGallery, setCanManageGallery] = useState<boolean>(false);
  const [canOpenGallerySetup, setCanOpenGallerySetup] = useState<boolean>(false);
  const [canUseGallerySetupManagementSections, setCanUseGallerySetupManagementSections] = useState<boolean>(false);
  const [canManageGalleryCollaborators, setCanManageGalleryCollaborators] = useState<boolean>(false);
  const [canEditArtworkSlots, setCanEditArtworkSlots] = useState<boolean>(false);
  const [canEditPseudoMoMArtworkSlots, setCanEditPseudoMoMArtworkSlots] = useState<boolean>(false);
  const [canUseArtworkRepositoryActions, setCanUseArtworkRepositoryActions] = useState<boolean>(true);
  const [forcedExternalSources, setForcedExternalSources] = useState<string[]>([]);
  const [offlineModeState, setOfflineModeState] = useState<OfflineModeState>(ONLINE_OFFLINE_MODE_STATE);

  const [isBackgroundSoundLoading, setIsBackgroundSoundLoading] = useState<boolean>(true);

  const [isAdminAsSeller, setIsAdminAsSeller] = useState<boolean>(false);
  const [showGalleryStatsPanel, setShowGalleryStatsPanel] = useState<boolean>(false);

  const personalRef = useRef(personal);
  useEffect(() => { personalRef.current = personal; }, [personal]);
  const isAdminAsSellerRef = useRef(isAdminAsSeller);
  useEffect(() => { isAdminAsSellerRef.current = isAdminAsSeller; }, [isAdminAsSeller]);
  const canEditArtworkSlotsRef = useRef(canEditArtworkSlots);
  useEffect(() => { canEditArtworkSlotsRef.current = canEditArtworkSlots; }, [canEditArtworkSlots]);
  const canEditPseudoMoMArtworkSlotsRef = useRef(canEditPseudoMoMArtworkSlots);
  useEffect(() => { canEditPseudoMoMArtworkSlotsRef.current = canEditPseudoMoMArtworkSlots; }, [canEditPseudoMoMArtworkSlots]);
  const offlineModeRef = useRef(offlineModeState.isOffline);
  useEffect(() => { offlineModeRef.current = offlineModeState.isOffline; }, [offlineModeState.isOffline]);
  const canUsePseudoMoMPillarActionsRef = useRef(false);
  const persistedPseudoMomActiveArtworkIndexesRef = useRef<Record<string, number>>({});
  const galleryTelemetryContextRef = useRef<TelemetrySpaceContext>({});
  const trackedGalleryViewRef = useRef<string | null>(null);
  const galleryKeyboardBlockedRef = useRef(false);
  const galleryKeyboardWasBlockedRef = useRef(false);

  const apiClient = ApiClient(BASE_API);

  const { id } = useParams();
  const [moods, setMoods] = useState<any[]>([]);
  const [published, setPublished] = useState<boolean>(false);
  const [collaborators, setCollaborators] = useState<Array<{ userId: string; email: string; role: "editor" | "contributor" }>>([]);

  const refetchCurrentGalleryData = async () => {
    const resp = await apiClient.getGalleryById(id);
    if (resp?.status && resp.gallery) {
      appData.setGalleryData({ ...appData.galleryData, ...resp.gallery });
      setCollaborators(resp.gallery.collaborators || []);
    }
  };


  const location = useLocation();
  const canvasRef = useRef(null);
  const pseudoMomIntroAnimationFrameRef = useRef<number | null>(null);
  const { listedVouchers } = useSelector((state: AppState) => state.nft);

  const { user } = useSelector((state: AppState) => state.auth);
  const autoLogin = useAutoLogin();

  const [renderSettingsModal, setRenderSettingsModal] = useState<boolean>(false);
  const [artworkType, setArtworkType] = useState<string>("");
  const [isToggleOn, setIsToggleOn] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("snapToWall");
      if (stored !== null) {
        return stored === "true";
      }
    }
    return false;
  });
  const [isMovingArtworks, setIsMovingArtworks] = useState<boolean>(false);
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
  const [movingActionLoading, setMovingActionLoading] = useState<boolean>(false);

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [galleryStats, setGalleryStats] = useState<GalleryStats>(() => normalizeGalleryStats(null));
  const [spaceProps, setSpaceProps] = useState<Prop[]>([]);
  const [savingPropIndexes, setSavingPropIndexes] = useState<number[]>([]);

  // Add file handling methods
  const getFilesFromFolder = async (folder: string): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      apiClient.get3DFileUrls(folder).then((result) => {
        resolve(result)
      })
    })
  };

  const uploadFilesToFolder = async (folder: string, files: File[]): Promise<{ name: string, url: string }[]> => {
    return await apiClient.upload3Dfiles(folder, files);
  };

  React.useEffect(() => {
    if (!user && searchParams.get("t") === "personal") {
      autoLogin();
    }
  }, [autoLogin, searchParams, user]);

  React.useEffect(() => {
    const hasTouch =
      (typeof window !== "undefined" && "ontouchstart" in window) ||
      (typeof navigator !== "undefined" && (navigator as any).maxTouchPoints > 0);
    setIsTouchDevice(!!hasTouch);
  }, []);

  const [main3DManager, setMain3DManager] = React.useState<
    Main3DManager | undefined
  >();

  const successNotify = (msg: any) => {
    toast.success(msg, {
      position: "bottom-left",
    })
  }

  const failedNotify = (msg: string) => {
    toast.error(msg, {
      position: "bottom-left",
    })
  }

  const hideLoadingScreen = () => {
    const loadingText = document.getElementById("loading");
    if (loadingText) {
      loadingText.classList.remove("pseudo-mom-intro-loading");
      loadingText.style.opacity = "0.0";
      loadingText.style.pointerEvents = "none";
    }
  };

  const updateOfflineModeState = useCallback((state: OfflineModeState) => {
    setOfflineModeState(state);
    onOfflineModeChange?.(state);
  }, [onOfflineModeChange]);

  const showOfflineUnavailable = useCallback((requestedGalleryId?: string, message?: string) => {
    const cachedGallery = getOfflineGalleryMetadata();
    updateOfflineModeState({
      status: "unavailable",
      isOffline: true,
      cachedGallery,
      message: message || getOfflineGalleryFailureMessage(cachedGallery, requestedGalleryId),
    });
    hideLoadingScreen();
  }, [updateOfflineModeState]);

  const showPseudoMomIntroLoadingScreen = () => {
    const loadingText = document.getElementById("loading");
    if (!loadingText) return;

    loadingText.textContent = "Gallery loading...";
    loadingText.classList.add("pseudo-mom-intro-loading");
    loadingText.style.opacity = "1";
    loadingText.style.pointerEvents = "auto";
  };

  const hidePseudoMomIntroLoadingScreen = () => {
    const loadingText = document.getElementById("loading");
    if (!loadingText) return;

    loadingText.style.opacity = "0.0";
    loadingText.style.pointerEvents = "none";
    window.setTimeout(() => {
      loadingText.classList.remove("pseudo-mom-intro-loading");
    }, 550);
  };

  const runPseudoMomIntro = async (settings: {
    durationSeconds: number;
    introSound?: { url?: string; soundVolume?: number } | null;
  }) => {
    const loadingText = document.getElementById("loading");
    const webglContainer = canvasRef.current as HTMLElement | null;
    const webglCanvas = webglContainer?.querySelector("canvas") as HTMLElement | null;
    const blurTarget = webglCanvas || webglContainer;
    const parsedDurationSeconds = Number(settings.durationSeconds);
    const normalizedDurationSeconds = Number.isFinite(parsedDurationSeconds) ? parsedDurationSeconds : 5;
    const durationMs = Math.max(0, Math.min(30, normalizedDurationSeconds)) * 1000;

    showPseudoMomIntroLoadingScreen();

    if (pseudoMomIntroAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(pseudoMomIntroAnimationFrameRef.current);
      pseudoMomIntroAnimationFrameRef.current = null;
    }

    if (blurTarget) {
      blurTarget.style.transition = "";
      blurTarget.style.willChange = "filter";
      blurTarget.style.filter = durationMs > 0 ? "blur(14px)" : "blur(0px)";
    }

    let pseudoMomIntroActive = true;
    void main3DManager?.soundService?.playIntroSound(settings.introSound, () => pseudoMomIntroActive);

    const startTime = performance.now();
    await new Promise<void>((resolve) => {
      if (durationMs <= 0) {
        resolve();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (pseudoMomIntroAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(pseudoMomIntroAnimationFrameRef.current);
          pseudoMomIntroAnimationFrameRef.current = null;
        }
        resolve();
      };
      const timeoutId = window.setTimeout(finish, durationMs);

      if (!blurTarget) {
        return;
      }

      const step = (now: number) => {
        const progress = Math.min(1, (now - startTime) / durationMs);
        const blurPx = 14 * (1 - progress);
        blurTarget.style.filter = `blur(${blurPx.toFixed(2)}px)`;
        if (progress < 1) {
          pseudoMomIntroAnimationFrameRef.current = window.requestAnimationFrame(step);
          return;
        }
        window.clearTimeout(timeoutId);
        pseudoMomIntroAnimationFrameRef.current = null;
        finish();
      };

      pseudoMomIntroAnimationFrameRef.current = window.requestAnimationFrame(step);
    });

    pseudoMomIntroActive = false;
    await main3DManager?.soundService?.stopIntroSound(0);
    if (blurTarget) {
      blurTarget.style.filter = "";
      blurTarget.style.willChange = "";
    }
    hidePseudoMomIntroLoadingScreen();
  };

  const galleryKeyboardBlocked = isGalleryKeyboardBlocked({
    artworksModal,
    buyArtworkModal,
    createArtworkModal,
    gallerySetupModal,
    feedbackModalOpen,
    enquireModal,
    confirmDeleteHexagon,
    confirmOpenHexagon,
    renderSettingsModal,
  });
  galleryKeyboardBlockedRef.current = galleryKeyboardBlocked;
  const anyModalOpen = galleryKeyboardBlocked || renderSettingsModal;

  const setGalleryKeyboardAvailability = React.useCallback((availability: boolean) => {
    if (!main3DManager) return;

    main3DManager.setKeyAvailability(resolveGalleryKeyboardAvailability(
      availability,
      galleryKeyboardBlockedRef.current
    ));
  }, [main3DManager]);

  React.useEffect(() => {
    main3DManager?.rendererService?.setPseudoMoMModelRotationPaused?.(
      shouldPausePseudoMoMModelRotation(anyModalOpen, isMovingArtworks)
    );
  }, [main3DManager, anyModalOpen, isMovingArtworks]);

  React.useEffect(() => {
    if (!main3DManager) return;

    if (galleryKeyboardBlocked) {
      setGalleryKeyboardAvailability(false);
      galleryKeyboardWasBlockedRef.current = true;
      return;
    }

    if (galleryKeyboardWasBlockedRef.current) {
      setGalleryKeyboardAvailability(true);
      galleryKeyboardWasBlockedRef.current = false;
    }
  }, [main3DManager, galleryKeyboardBlocked, setGalleryKeyboardAvailability]);

  const onDioramaClicked = useCallback((slot: Slot) => {
    // if (!slot.artworkWrapper.galleryId) throw new Error('missing gallery id')
    // navigate(`/gallery/${slot.artworkWrapper.galleryId}`)
  }, [personal]);

  const onArtworkClicked = useCallback((slot: Slot) => {
    appData.restoreViewerRotation?.();
    const activeArtwork = slot.artworkWrapper?.artworks?.[slot.artworkWrapper.activeArtwork];
    const clickedSlotIndex = resolveClickedSlotIndex(slot);
    const trackActiveArtworkView = () => {
      if (!activeArtwork?._id) {
        return;
      }

      trackArtworkView(
        String(activeArtwork._id),
        `${location.pathname}${location.search || ""}`,
        id,
        searchParams.get("t"),
        galleryTelemetryContextRef.current
      );
    };

    const selectActiveArtwork = () => {
      const artworkWithWrapperId = (activeArtwork && slot.artworkWrapper?._id && (activeArtwork.type === "image" || activeArtwork.type === "video"))
        ? {
          ...activeArtwork,
          artworkData: {
            ...activeArtwork.artworkData,
            frame: {
              ...activeArtwork.artworkData?.frame,
              artworkWrapperID: activeArtwork.artworkData?.frame?.artworkWrapperID ?? slot.artworkWrapper._id
            }
          }
        }
        : activeArtwork;

      if (clickedSlotIndex === null) return false;

      setSelectedSlot(clickedSlotIndex.toString());
      setSelectedArtwork(artworkWithWrapperId);
      updateArtworkType(activeArtwork?.type || "");
      if (activeArtwork && activeArtwork.type === "3dmodel") {
        updateArtworkType("3dmodel");
      }
      return true;
    };

    if (offlineModeRef.current) {
      if (!selectActiveArtwork()) return;
      if (slot.artworkWrapper?.artworks?.length) {
        setBuyArtworkModal(true);
        setGalleryKeyboardAvailability(false);
      }
      return;
    }

    const artworkCount = slot.artworkWrapper?.artworks?.length || 0;
    const clickAction = resolveArtworkSlotClickAction(
      canEditArtworkSlotsRef.current,
      canEditPseudoMoMArtworkSlotsRef.current,
      Boolean(slot.artworkWrapper),
      artworkCount,
      canUsePseudoMoMPillarActionsRef.current
    );

    if (
      clickAction === "openDetailsModal" &&
      main3DManager?.isVRPresenting?.() &&
      !personalRef.current &&
      !isAdminAsSellerRef.current &&
      slot.artworkWrapper
    ) {
      const activeArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork];
      if (activeArtwork && (activeArtwork.type === "image" || activeArtwork.type === "video" || activeArtwork.type === "3dmodel")) {
        void resolveArtworkViewerMedia(activeArtwork, "gallery", {
          apiClient,
          baseAPI: BASE_API,
          artworkWrapper: slot.artworkWrapper,
          spaceData: appData.spaceData,
          mediaDelivery: main3DManager.getRuntimeSnapshot()?.mediaDelivery || null,
        }).then((resolvedMedia) => {
          if (!resolvedMedia) {
            failedNotify("Unable to open this artwork in VR.");
            return null;
          }
          return main3DManager.openVRViewer(resolvedMedia).then(() => {
            trackActiveArtworkView();
          });
        }).catch((error) => {
          console.error("Failed to resolve artwork media for VR viewer", error);
          failedNotify("Unable to open this artwork in VR.");
        });
        return;
      }
    }

    if (clickAction === "openListModal") {
      if (clickedSlotIndex === null) return;
      setSelectedSlot(clickedSlotIndex.toString());
      setSelectedArtwork(null);
      updateArtworkType("");
      setArtworksModal(true);
    } else if (clickAction === "openDetailsModal") {
      if (!selectActiveArtwork()) return;
      setBuyArtworkModal(true);
      trackActiveArtworkView();
    } else if (clickAction === "openEditModal") {
      if (!selectActiveArtwork()) return;
      setArtworksModal(true);
    }
    setGalleryKeyboardAvailability(false);
  }, [apiClient, failedNotify, id, location.pathname, location.search, main3DManager, searchParams, setGalleryKeyboardAvailability]);

  const onSpaceEditClicked = async (data: any) => {
    if (offlineModeRef.current) return;

    if (data.action === "add") {
      setSpaceToOpen(data.spaceNumber);
      const skipDialog = localStorage.getItem("skip-open-dialog");
      if (skipDialog !== "skip") {
        setConfirmOpenHexagon(true);
        return;
      }

      await onOpenHexagon(true, data.spaceNumber);
    } else {
      setSpaceToRemove(data.spaceNumber);
      const skipDialog = localStorage.getItem("skip-remove-dialog");
      if (skipDialog !== "skip") {
        setConfirmDeleteHexagon(true);
        return;
      }

      await onConfirmRemoveHexagon(true, data.spaceNumber);
    }
  }

  const updateArtworkDB = async (artwork: Artwork) => {
    if (offlineModeRef.current) return;

    const { status } = await apiClient.editArtwork(artwork._id, artwork);
    if (status) {
      successNotify("Artwork has been updated");
    } else {
      failedNotify("Failed to update artwork");
    }

  }

  const onConfirmRemoveHexagon = async (skip: boolean, space: number) => {
    if (offlineModeRef.current) return;

    if (skip) {
      localStorage.setItem("skip-remove-dialog", "skip");
    }

    try {
      // find slots from space data
      const spaceName = `Space-${space + 1}`;
      const slotIndecesToUnlist = appData.spaceData.data.slots.filter(slot => slot.owner === spaceName).map(slot => slot.index);

      const artworksWrapperToRemove = appData.galleryData.artworkWrappers.filter(
        wrapper => slotIndecesToUnlist.find(i => wrapper.slotTarget === i) && wrapper.artworks.length > 0
      );
      let _artworks: { status: boolean, artworks: Artwork[] };
      if (isAdminAsSellerRef.current) {
        _artworks = await apiClient.getArtworksByUserId(appData.galleryData.user);
      } else if (personalRef.current) {
        _artworks = await apiClient.getArtworksByUser();
      }
      const wrapperIdsToRemove = artworksWrapperToRemove.map(ia => {
        const art = _artworks.artworks.find(i => i._id === ia.artworks[ia.activeArtwork]._id);
        if (art) {
          art.onsale = false;
        }
        return ia._id;
      });


      const { status, stats } = await apiClient.editHexaProcGallery(id, space, 0, wrapperIdsToRemove);
      if (status) {
        setGalleryStats(normalizeGalleryStats(stats, appData.galleryData?.artworkLoadQuotaMb));
        // remove it from artworks
        const newWrappers = appData.galleryData.artworkWrappers.map(wrapper => {
          if (wrapperIdsToRemove.includes(wrapper._id)) {
            wrapper.artworks = [];
          }
          return wrapper;
        });
        appData.galleryData.artworkWrappers = newWrappers;
        appData.galleryData.data.spaceProceduralParameters[space] = false;

        setConfirmDeleteHexagon(false);

        main3DManager.rendererService.addToRenderQueue();
        setArtworks(_artworks.artworks);
        main3DManager.eventService.artworkLoaderService.loadArtworks().then(() => {
          _3dUtil.handleProceduralSpaceVisibility(appData);
          main3DManager.rendererService.addToRenderQueue();
        });
      } else {
        failedNotify("Cannnot remove hexagon");
      }
    } catch (error) {
      console.log(error);
      failedNotify("Cannnot remove hexagon");
    }
  }

  const onOpenHexagon = async (skip: boolean, _spaceToOpen: number) => {
    if (offlineModeRef.current) return;

    if (skip) {
      localStorage.setItem("skip-open-dialog", "skip");
    }

    const { status, wrappersToRemove, stats } = await apiClient.editHexaProcGallery(id, _spaceToOpen);
    setGalleryStats(normalizeGalleryStats(stats, appData.galleryData?.artworkLoadQuotaMb));

    if (status && wrappersToRemove && wrappersToRemove.length > 0) {
      let _artworks: { status: boolean, artworks: Artwork[] };
      if (isAdminAsSellerRef.current) {
        _artworks = await apiClient.getArtworksByUserId(appData.galleryData.user);
      } else if (personalRef.current) {
        _artworks = await apiClient.getArtworksByUser();
      }
      const artworksToRemove = appData.galleryData.artworkWrappers.flatMap(w => {
        if (wrappersToRemove.includes(w._id) && w.artworks.length > 0) {
          return w.artworks.map(a => a._id)
        }
        return [];
      })

      for (const artworkToRemove of artworksToRemove) {
        const art = _artworks.artworks.find(i => i._id === artworkToRemove);
        if (!art) continue;
        art.onsale = false;
        // art.slotTarget = -1;
      }
      setArtworks(_artworks.artworks);


      const newWrappers = appData.galleryData.artworkWrappers.map(wrapper => {
        if (wrapper.artworks.length > 0 && !wrappersToRemove.includes(wrapper._id)) {
          return wrapper;
        }
        return {
          ...wrapper,
          artworks: []
        };
      })
      appData.galleryData.artworkWrappers = newWrappers;


    }
    appData.galleryData.data.spaceProceduralParameters[_spaceToOpen] = true;
    setConfirmOpenHexagon(false);
    main3DManager.rendererService.addToRenderQueue();
    main3DManager.eventService.artworkLoaderService.loadArtworks().then(() => {
      _3dUtil.handleProceduralSpaceVisibility(appData);
      main3DManager.rendererService.addToRenderQueue();
    });

  }
  // Initializes main 3d manager on id change and also destorys it on change
  React.useEffect(() => {

    if (!id) return;

    startGalleryBenchmark(location.search, process.env.REACT_APP_GALLERY_BENCHMARK, "1");
    let _main3D = new Main3DManager();
    appData.setMain3DManager(_main3D);

    setMain3DManager(_main3D);

    return () => {
      beginGalleryBenchmarkTeardown();
      _main3D.destroy();
      main3DManager?.destroy();
      recordGalleryBenchmarkRendererSnapshot("teardown", {});
      completeGalleryBenchmarkTeardown();
      appData.setMain3DManager(null);
    };
  }, [id]);

  React.useEffect(() => {
    if (main3DManager && canvasRef.current) {
      main3DManager.init(canvasRef.current);
      main3DManager.soundService?.enableAudioUnlockOnUserGesture?.();
      main3DManager.setDioramaClickEvent(onDioramaClicked);
      main3DManager.setArtworkClickEvent(onArtworkClicked);
      main3DManager.setSpaceEditClickEvent(onSpaceEditClicked);
    }
  }, [main3DManager, canvasRef]);

  React.useEffect(() => {
    if (main3DManager) {
      main3DManager.setVRFailureEvent((reason) => {
        if (reason) {
          failedNotify(reason);
        }
      });
    }
  }, [failedNotify, main3DManager]);

  useEvents(main3DManager, canvasRef, listedVouchers);

  React.useEffect(() => {
    let isCurrentInit = true;

    if (main3DManager && WEBGL.isWebGLAvailable()) {

      const init3D = async () => {
        const galleryLoadStartedAt = Date.now();
        try {
          setIsPseudoMoMGallery(false);
          setHasPlayableGalleryBackgroundMusic(false);
          const routeMode = searchParams.get("t");
          const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
          const isOfflineEligibleRoute = isOfflineEligiblePublicGalleryRoute(location.pathname, location.search);
          const requestedGalleryId = getGalleryRouteId(location.pathname) || id;
          const cachedOfflineGallery = await syncOfflineGalleryMetadataFromCache();
          if (!isCurrentInit) return;
          if (!isOnline && requestedGalleryId && isAuthRequiredGalleryMode(routeMode)) {
            showOfflineUnavailable(
              requestedGalleryId,
              "Admin and personal gallery modes require a network connection. Public offline replay is read-only."
            );
            return;
          }
          const canReplayCachedPublicGallery = canUseCachedPublicGalleryReplay(
            cachedOfflineGallery,
            requestedGalleryId,
            isOfflineEligibleRoute
          );
          let isCachedPublicReplay = !isOnline && canReplayCachedPublicGallery;

          updateOfflineModeState(!isOnline && isOfflineEligibleRoute
            ? {
              status: "active",
              isOffline: true,
              cachedGallery: cachedOfflineGallery,
              message: "Showing the last gallery saved on this device.",
            }
            : {
              ...ONLINE_OFFLINE_MODE_STATE,
              cachedGallery: cachedOfflineGallery,
            }
          );

          const galleryResp = await apiClient.getGalleryById(id);
          markGalleryBenchmark("galleryResponseMs");
          if (!isCurrentInit) return;
          if (shouldShowOfflineUnavailableForBootstrapFailure(galleryResp, isOnline, isOfflineEligibleRoute)) {
            showOfflineUnavailable(requestedGalleryId);
            return;
          }
          let galleryData = ensureGalleryBootstrapStep(
            galleryResp,
            "Failed to load gallery data.",
            (response) => response.gallery
          );
          const galleryRespWasOfflineReplay = Boolean((galleryResp as any)?.__offlineReplay) ||
            (!isOnline && isOfflineEligibleRoute && Boolean(galleryResp?.status));
          if (
            galleryRespWasOfflineReplay &&
            requestedGalleryId &&
            galleryData?._id &&
            requestedGalleryId !== String(galleryData._id)
          ) {
            throw new Error("Cached public gallery replay is not available for this gallery route.");
          }
          isCachedPublicReplay = isCachedPublicReplay || galleryRespWasOfflineReplay;
          setPublished(Boolean(galleryData.published));
          setTitle(galleryData.title);
          setGalleryStats(normalizeGalleryStats(galleryData.stats, galleryData.artworkLoadQuotaMb));
          setCollaborators(galleryData.collaborators || []);

          const skipCurrentUserLookup = shouldSkipGalleryCurrentUserLookup(
            routeMode,
            isOnline && !galleryRespWasOfflineReplay,
            isOfflineEligibleRoute,
            hasValidAccessToken(window.localStorage.getItem("accessToken"))
          );
          const currentUserResponse = skipCurrentUserLookup ? null : await apiClient.getMe();
          if (!isCurrentInit) return;
          const currentUserResult = skipCurrentUserLookup
            ? { currentUser: null, errorMessage: null, shouldRecoverUnauthorized: false }
            : resolveGalleryCurrentUser(routeMode, currentUserResponse);
          if (currentUserResult.shouldRecoverUnauthorized) {
            handleUnauthorizedSession();
            hideLoadingScreen();
            return;
          }
          if (currentUserResult.errorMessage) {
            failedNotify(currentUserResult.errorMessage);
            hideLoadingScreen();
            return;
          }
          const currentUser = currentUserResult.currentUser;
          appData.setUserData(currentUser);

          const viewerContext = resolveGalleryViewerContext(routeMode, currentUser, galleryData.user, galleryData.collaborators);
          setAdmin(viewerContext.admin);
          setIsAdminAsSeller(viewerContext.isAdminAsSeller);
          setPersonal(viewerContext.personal || viewerContext.isAdminAsSeller);
          const personalMode = viewerContext.personal || viewerContext.isAdminAsSeller;

          const spaceResp = await apiClient.getSpaceById(galleryData.spaceId);
          markGalleryBenchmark("spaceResponseMs");
          if (!isCurrentInit) return;
          if (shouldShowOfflineUnavailableForBootstrapFailure(spaceResp, isOnline, isOfflineEligibleRoute)) {
            showOfflineUnavailable(requestedGalleryId);
            return;
          }
          const spaceData = ensureGalleryBootstrapStep(
            spaceResp,
            "Failed to load gallery space.",
            (response) => response.space
          );
          const shouldRunIntro = shouldRunGalleryIntro(spaceData, galleryData);
          if (shouldRunIntro) {
            showPseudoMomIntroLoadingScreen();
            main3DManager.soundService?.preloadIntroSound?.(galleryData.data?.pseudoMomIntroSound ?? null);
          }

          setShowGalleryStatsPanel(shouldShowGalleryStatsPanel(personalMode, spaceData, galleryData));
          const canEditSlots = resolveCanEditArtworkSlots(routeMode, viewerContext, spaceData);
          const canEditPseudoMoMAsAdmin = canAdminEditPseudoMoMArtworkSlots(routeMode, viewerContext, spaceData);
          const canManageGallerySettings = resolveCanManageGallerySettings(routeMode, viewerContext, spaceData);
          const canOpenSetup = resolveCanOpenGallerySetup(routeMode, viewerContext, spaceData);
          const canManageCollaborators = resolveCanManageGalleryCollaborators(routeMode, viewerContext, spaceData);
          const canUsePseudoMoMPillarActions = isPseudoMoMSpace(spaceData) && canEditSlots;
          canEditArtworkSlotsRef.current = canEditSlots;
          canEditPseudoMoMArtworkSlotsRef.current = canEditPseudoMoMAsAdmin;
          canUsePseudoMoMPillarActionsRef.current = canUsePseudoMoMPillarActions;
          setCanManageGallery(canManageGallerySettings);
          setCanOpenGallerySetup(canOpenSetup);
          setCanUseGallerySetupManagementSections(canOpenSetup);
          setCanManageGalleryCollaborators(canManageCollaborators);
          setCanEditArtworkSlots(canEditSlots);
          setCanEditPseudoMoMArtworkSlots(canEditPseudoMoMAsAdmin);
          setCanUseArtworkRepositoryActions(true);
          setForcedExternalSources(canUsePseudoMoMPillarActions ? ["europeana", "sketchfab"] : []);
          main3DManager.setViewerRotationEnabled(
            ENABLE_3D_ARTWORK_DRAG_ROTATION &&
            galleryData.data?.pseudoMomManualRotateObjects === true &&
            routeMode !== "personal" &&
            routeMode !== "admin"
          );

          let _artworks: { status: boolean, artworks: Artwork[] };
          if (isCachedPublicReplay) {
            _artworks = {
              status: true,
              artworks: collectGalleryArtworksFromWrappers<Artwork>(galleryData.artworkWrappers),
            };
          } else if (viewerContext.isAdminAsSeller || canEditPseudoMoMAsAdmin) {
            _artworks = await apiClient.getArtworksByUserId(galleryData.user);
          } else if (viewerContext.personal) {
            _artworks = await apiClient.getArtworksByUser();
          } else {
            _artworks = await apiClient.getArtworksByGallery(id);
            if (!isCurrentInit) return;
            if (shouldShowOfflineUnavailableForBootstrapFailure(_artworks, isOnline, isOfflineEligibleRoute)) {
              showOfflineUnavailable(requestedGalleryId);
              return;
            }
            if (shouldUseCachedPublicGalleryArtworks(_artworks, isCachedPublicReplay)) {
              isCachedPublicReplay = true;
              _artworks = {
                status: true,
                artworks: collectGalleryArtworksFromWrappers<Artwork>(galleryData.artworkWrappers),
              };
            }
          }
          if (!isCurrentInit) return;
          const artworksResponse = ensureGalleryBootstrapStep(
            _artworks,
            "Failed to load artworks.",
            (response) => response.artworks
          );
          markGalleryBenchmark("artworksResponseMs");
          setArtworks(artworksResponse);

          const parsedMood = Number.parseInt(String(galleryData?.data?.mood), 10);
          const fallbackMood = Number.isInteger(parsedMood) && parsedMood >= 0 ? parsedMood : undefined;
          const resolvedMood = resolveActiveMoodIndex(
            spaceData?.data?.moods,
            fallbackMood,
            {
              userId: currentUser?._id,
              galleryId: galleryData?._id,
              isAdmin: viewerContext.admin || viewerContext.isAdminAsSeller,
              includeUnpublished: viewerContext.admin || viewerContext.isAdminAsSeller,
              includeAssignedUnpublished: true
            }
          );
          if (!galleryData.data) {
            galleryData.data = {} as any;
          }
          galleryData.data.mood = resolvedMood;
          appData.setGalleryData({ ...galleryData, editable: canEditSlots });
          setHasPlayableGalleryBackgroundMusic(
            isGalleryBackgroundMusicPlayable(galleryData.data?.sound)
          );
          persistedPseudoMomActiveArtworkIndexesRef.current = (galleryData.artworkWrappers || []).reduce(
            (activeArtworkIndexes: Record<string, number>, wrapper: ArtworkWrapper) => {
              if (wrapper?._id) {
                const activeArtworkIndex = Number(wrapper.activeArtwork);
                activeArtworkIndexes[String(wrapper._id)] = Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0;
              }
              return activeArtworkIndexes;
            },
            {}
          );

          appData.setSpaceData(spaceData);
          setIsPseudoMoMGallery(isPseudoMoMSpace(spaceData));
          setMoods(spaceData.data.moods || []);
          setSpaceProps(spaceData.data.props || []);
          const telemetryContext = buildTelemetrySpaceContext(galleryData, spaceData);
          galleryTelemetryContextRef.current = telemetryContext;
          const galleryTelemetryKey = `${galleryData?._id || requestedGalleryId}:${location.pathname}${location.search || ""}`;
          const telemetryPath = `${location.pathname}${location.search || ""}`;
          const shouldTrackGalleryTelemetry = (
            isOnline &&
            !galleryRespWasOfflineReplay &&
            !isCachedPublicReplay &&
            galleryData?._id &&
            trackedGalleryViewRef.current !== galleryTelemetryKey
          );
          const trackArtworkRuntimeTelemetry = (
            snapshot: ArtworkLoadTelemetrySnapshot
          ) => {
            if (!shouldTrackGalleryTelemetry) return;

            void getRuntimeConfig().then((runtimeConfig) => {
              if (
                snapshot.phase === "initial_budget" &&
                !isCurrentInit
              ) return;
              if (!shouldSampleGalleryPerformance(runtimeConfig)) return;

              const qualityProfile = getStoredRenderQuality();
              const qualityCaps = runtimeConfig.renderer.qualityCaps[qualityProfile];
              trackGalleryPerformance(
                telemetryPath,
                {
                  phase: snapshot.phase === "initial_budget"
                    ? "artwork_initial_budget"
                    : "artwork_cleanup",
                  qualityProfile,
                  pixelRatio: qualityCaps.pixelRatio,
                  postProcessingScale: qualityCaps.postProcessingScale,
                  shadowMapSize: qualityCaps.shadowMapSize,
                  activeArtworkCount: snapshot.activeArtworkCount,
                  visibleArtworkCount: snapshot.visibleArtworkCount,
                  pendingArtworkCount: snapshot.pendingArtworkCount,
                  disposedArtworkCount: snapshot.disposedArtworkCount,
                  staleArtworkCleanupCount: snapshot.staleArtworkCleanupCount,
                  loadStrategy: snapshot.loadStrategy,
                },
                String(galleryData._id),
                routeMode,
                telemetryContext
              );
            });
          };
          if (shouldTrackGalleryTelemetry) {
            trackedGalleryViewRef.current = galleryTelemetryKey;
            trackGalleryView(
              String(galleryData._id),
              telemetryPath,
              routeMode,
              telemetryContext
            );
          }

          console.log("%cLoading data: ", "color:green", appData)
          let pseudoMomIntroStarted = false;
          let pseudoMomIntroPromise: Promise<void> | null = null;
          const startPseudoMomIntro = () => {
            if (pseudoMomIntroStarted) return;
            pseudoMomIntroStarted = true;
            pseudoMomIntroPromise = runPseudoMomIntro({
              durationSeconds: galleryData.data?.pseudoMomIntroDurationSeconds ?? 5,
              introSound: galleryData.data?.pseudoMomIntroSound ?? null
            })
              .finally(async () => {
                await main3DManager.loadBackgroundSound();
                setGalleryKeyboardAvailability(true);
                main3DManager.rendererService?.addToRenderQueue?.();
                setIsBackgroundSoundLoading(false);
              });
          };
          setGalleryKeyboardAvailability(false);
          let galleryLoadError: unknown = null;
          const galleryLoadResult = await main3DManager.loadGallery({
            keepLoadingScreen: shouldRunIntro,
            onSceneReady: shouldRunIntro
              ? startPseudoMomIntro
              : () => {
                setGalleryKeyboardAvailability(true);
              },
            startBackgroundSound: !shouldRunIntro,
            onLoadError: (error) => {
              if (!isCurrentInit) return;
              galleryLoadError = error;
            },
            onArtworkTelemetry: trackArtworkRuntimeTelemetry,
          });
          const actualLoadStrategy = (
            galleryLoadResult &&
            typeof galleryLoadResult === "object" &&
            "loadStrategy" in galleryLoadResult &&
            galleryLoadResult.loadStrategy === "progressive"
          ) ? "progressive" : "all";
          const galleryReadyAt = Date.now();
          markGalleryBenchmark("galleryReadyMs");
          if (shouldTrackGalleryTelemetry) {
            void getRuntimeConfig().then((runtimeConfig) => {
              if (!isCurrentInit || !shouldSampleGalleryPerformance(runtimeConfig)) return;

              const qualityProfile = getStoredRenderQuality();
              const qualityCaps = runtimeConfig.renderer.qualityCaps[qualityProfile];
              trackGalleryPerformance(
                telemetryPath,
                {
                  phase: "gallery_load",
                  qualityProfile,
                  pixelRatio: qualityCaps.pixelRatio,
                  postProcessingScale: qualityCaps.postProcessingScale,
                  shadowMapSize: qualityCaps.shadowMapSize,
                  galleryLoadMs: Math.max(0, galleryReadyAt - galleryLoadStartedAt),
                  activeAssetCount: Array.isArray(artworksResponse) ? artworksResponse.length : 0,
                  loadStrategy: actualLoadStrategy,
                },
                String(galleryData._id),
                routeMode,
                telemetryContext
              );
            });
          }
          if (main3DManager.rendererService?.renderer?.info) {
            recordGalleryBenchmarkRendererSnapshot("loaded", main3DManager.rendererService.renderer.info);
          }
          setIsBackgroundSoundLoading(false);
          if (!isCurrentInit) return;

          if (shouldRunIntro) {
            if (!pseudoMomIntroPromise) {
              startPseudoMomIntro();
            }
            await pseudoMomIntroPromise;
            if (!isCurrentInit) return;
          } else {
            setGalleryKeyboardAvailability(true);
          }

          if (galleryLoadError && isOfflineEligibleRoute) {
            if ((!isOnline || isCachedPublicReplay) && isOfflineEligibleRoute) {
              showOfflineUnavailable(requestedGalleryId, "This gallery could not be reopened from the offline cache.");
              return;
            }

            if (isOnline && isOfflineEligibleRoute) {
              updateOfflineModeState({
                status: "failed",
                isOffline: false,
                cachedGallery: getOfflineGalleryMetadata(),
                message: "Gallery opened online, but not all required assets loaded for offline viewing.",
              });
            }
            return;
          }

          if ((!isOnline || isCachedPublicReplay) && isOfflineEligibleRoute) {
            updateOfflineModeState({
              status: "active",
              isOffline: true,
              cachedGallery: cachedOfflineGallery,
              message: "Showing the last gallery saved on this device.",
            });
            return;
          }

          if (isOnline && isOfflineEligibleRoute) {
            updateOfflineModeState({
              status: "preparing",
              isOffline: false,
              cachedGallery: cachedOfflineGallery,
              message: "Preparing this public gallery for offline viewing.",
            });

            const preparationResult = await prepareOfflineGallery(buildOfflineGalleryManifest({
              baseApi: BASE_API,
              gallery: galleryData,
              routePath: `${location.pathname}${location.search}`,
              space: spaceData,
            }));
            if (!isCurrentInit) return;

            if (preparationResult.ok) {
              updateOfflineModeState({
                status: "ready",
                isOffline: false,
                cachedGallery: preparationResult.metadata,
                message: "This public gallery is ready for offline viewing.",
              });
            } else {
              updateOfflineModeState({
                status: "failed",
                isOffline: false,
                cachedGallery: "previousMetadata" in preparationResult
                  ? preparationResult.previousMetadata
                  : cachedOfflineGallery,
                message: "error" in preparationResult
                  ? preparationResult.error
                  : "Failed to prepare this gallery for offline viewing.",
              });
            }
          }
        } catch (error) {
          if (!isCurrentInit) return;
          recordGalleryBenchmarkError("GALLERY_BOOTSTRAP_ERROR", location.pathname);
          console.error("Gallery: Error initializing 3D scene", error);
          const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
          const requestedGalleryId = getGalleryRouteId(location.pathname) || id;
          const failedRouteMode = searchParams.get("t");
          if (!isOnline && requestedGalleryId && isAuthRequiredGalleryMode(failedRouteMode)) {
            showOfflineUnavailable(
              requestedGalleryId,
              "Admin and personal gallery modes require a network connection. Public offline replay is read-only."
            );
            return;
          }
          if (!isOnline && isOfflineEligiblePublicGalleryRoute(location.pathname, location.search)) {
            showOfflineUnavailable(requestedGalleryId);
            return;
          }
          failedNotify("Failed to initialize gallery.");
          hideLoadingScreen();
        }
      }
      init3D()
    }

    return () => {
      isCurrentInit = false;
      if (pseudoMomIntroAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(pseudoMomIntroAnimationFrameRef.current);
        pseudoMomIntroAnimationFrameRef.current = null;
      }
      if (main3DManager) {
        void main3DManager.soundService?.stopIntroSound?.(0);
        main3DManager.dispose();
      }
      // Clean up interval on unmount
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      setIsPseudoMoMGallery(false);
      setHasPlayableGalleryBackgroundMusic(false);
    };
  }, [main3DManager, setGalleryKeyboardAvailability, setHasPlayableGalleryBackgroundMusic, setIsPseudoMoMGallery]);

  // after list artwork on the wall, we close pop-up
  const onListingArtworkHandler = async (artworkData: any, _galleryStats: any) => {
    if (offlineModeRef.current) return false;

    const galleryData = appData.galleryData;
    const normalizeSceneSlotTarget = (value: any): number | null => {
      const target = Number(value);
      return Number.isFinite(target) && target >= 0 ? target : null;
    };
    setGalleryStats(normalizeGalleryStats(_galleryStats, galleryData?.artworkLoadQuotaMb));

    if (artworkData.listing) {
      // listing action
      // Update frame artworkWrapperID for all artworks in the wrapper
      const listedWrapper = galleryData.artworkWrappers.find(w => w._id === artworkData._id || w._id === artworkData.wrapper?._id || w.slotTarget === artworkData.slotTarget);
      const incomingArtwork = artworkData.artworks?.[0];
      const shouldAppendFallback = canEditPseudoMoMArtworkSlotsRef.current && incomingArtwork && listedWrapper?.artworks;
      const fallbackArtworks = shouldAppendFallback && !listedWrapper.artworks.some((artwork: Artwork) => artwork._id === incomingArtwork._id)
        ? [...listedWrapper.artworks, incomingArtwork]
        : (artworkData.artworks || listedWrapper?.artworks || []);
      const wrapperToLoad: ArtworkWrapper = {
        ...listedWrapper,
        ...artworkData,
        ...artworkData.wrapper,
        _id: artworkData.wrapper?._id || artworkData._id || listedWrapper?._id,
        artworks: artworkData.wrapper?.artworks || fallbackArtworks,
        activeArtwork: artworkData.wrapper?.activeArtwork ?? artworkData.activeArtwork ?? listedWrapper?.activeArtwork ?? 0,
        transforms: {
          ...listedWrapper?.transforms,
          ...artworkData.transforms,
          ...artworkData.wrapper?.transforms,
          position: {
            x: 0,
            y: 0,
            z: 0,
            ...listedWrapper?.transforms?.position,
            ...artworkData.transforms?.position,
            ...artworkData.wrapper?.transforms?.position
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0,
            ...listedWrapper?.transforms?.rotation,
            ...artworkData.transforms?.rotation,
            ...artworkData.wrapper?.transforms?.rotation
          },
          scale: {
            x: 1,
            y: 1,
            z: 1,
            ...listedWrapper?.transforms?.scale,
            ...artworkData.transforms?.scale,
            ...artworkData.wrapper?.transforms?.scale
          }
        }
      };

      if (wrapperToLoad.artworks && wrapperToLoad._id) {
        wrapperToLoad.artworks.forEach(artwork => {
          if (artwork.artworkData && artwork.artworkData.frame) {
            artwork.artworkData.frame.artworkWrapperID = wrapperToLoad._id;
          }
        });
      }

      if (listedWrapper) {
        Object.assign(listedWrapper, wrapperToLoad);
      } else {
        galleryData.artworkWrappers.push(wrapperToLoad);
      }
      if (wrapperToLoad._id && persistedPseudoMomActiveArtworkIndexesRef.current[String(wrapperToLoad._id)] === undefined) {
        const activeArtworkIndex = Number(wrapperToLoad.activeArtwork);
        persistedPseudoMomActiveArtworkIndexesRef.current[String(wrapperToLoad._id)] = Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0;
      }
      const activeArtworkIndex = Number(wrapperToLoad.activeArtwork);
      const activeArtwork = wrapperToLoad.artworks?.[
        Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0
      ];
      setSelectedArtwork(activeArtwork || selectedArtwork);
      appData.setGalleryData({ ...galleryData, artworkWrappers: [...galleryData.artworkWrappers] });
      await main3DManager.loadArtwork(wrapperToLoad.slotTarget, wrapperToLoad, true);
    } else {
      // unlisting
      const requestSlotTarget = normalizeSceneSlotTarget(artworkData.slotTarget);
      const responseSlotTarget = normalizeSceneSlotTarget(artworkData.wrapper?.slotTarget);
      const lookupSlotTarget = requestSlotTarget ?? responseSlotTarget;
      const unlistedWrapper = galleryData.artworkWrappers.find(a => Number(a.slotTarget) === lookupSlotTarget);
      if (
        canEditPseudoMoMArtworkSlotsRef.current &&
        isInvalidPseudoMoMRemoveResponse(unlistedWrapper, artworkData.wrapper)
      ) {
        failedNotify("Failed to remove artwork from pillar");
        await refetchCurrentGalleryData();
        return false;
      }

      if (artworkData.wrapper?.artworks?.length) {
        const wrapperToLoad = {
          ...unlistedWrapper,
          ...artworkData.wrapper
        };
        if (unlistedWrapper) {
          Object.assign(unlistedWrapper, wrapperToLoad);
        } else {
          galleryData.artworkWrappers.push(wrapperToLoad);
        }
        const nextActiveArtwork = Number(wrapperToLoad.activeArtwork);
        const nextArtwork = wrapperToLoad.artworks?.[
          Number.isFinite(nextActiveArtwork) ? nextActiveArtwork : 0
        ];
        setSelectedArtwork(nextArtwork || null);
        appData.setGalleryData({ ...galleryData, artworkWrappers: [...galleryData.artworkWrappers] });
        await main3DManager.loadArtwork(wrapperToLoad.slotTarget, wrapperToLoad, true);
      } else {
        const slotTargetToClear = requestSlotTarget ?? normalizeSceneSlotTarget(unlistedWrapper?.slotTarget) ?? responseSlotTarget;
        if (slotTargetToClear === null) {
          failedNotify("Failed to remove artwork from placeholder");
          await refetchCurrentGalleryData();
          return false;
        }
        const wrapperToClear = {
          ...(unlistedWrapper || {}),
          ...(artworkData.wrapper || {}),
          slotTarget: slotTargetToClear,
          activeArtwork: 0,
          artworks: []
        };
        if (unlistedWrapper) {
          Object.assign(unlistedWrapper, wrapperToClear);
        } else if (Number.isFinite(Number(wrapperToClear.slotTarget))) {
          galleryData.artworkWrappers.push(wrapperToClear as ArtworkWrapper);
        }
        const removedArtwork = artworkData.artwork
          ? {
            ...artworkData.artwork,
            onsale: false,
            listed: false
          }
          : null;
        setSelectedArtwork(removedArtwork);
        if (removedArtwork?._id) {
          setArtworks((previousArtworks) => previousArtworks.map((previousArtwork) =>
            String(previousArtwork?._id) === String(removedArtwork._id) ? removedArtwork : previousArtwork
          ));
        }
        appData.setGalleryData({ ...galleryData, artworkWrappers: [...galleryData.artworkWrappers] });
        await main3DManager.loadArtwork(wrapperToClear.slotTarget, wrapperToClear as ArtworkWrapper, false);
        main3DManager.rendererService?.refreshPseudoMoMLightForSlot?.(wrapperToClear.slotTarget);
      }
    }

    let _artworks: { status: boolean, artworks: Artwork[] } | undefined;
    if (isAdminAsSellerRef.current || canEditPseudoMoMArtworkSlotsRef.current) {
      _artworks = await apiClient.getArtworksByUserId(appData.galleryData.user);
    } else if (personalRef.current) {
      _artworks = await apiClient.getArtworksByUser();
    }
    if (_artworks?.artworks) {
      setArtworks(_artworks.artworks);
    }
    // setArtworksModal(false);

    return true;
  }

  const onArtworkChangedHandler = async (slot: number, artwork: Artwork) => {
    if (offlineModeRef.current) return;

    const galleryData = appData.galleryData;
    // Find the existing wrapper and update it
    const existingWrapper = galleryData.artworkWrappers.find(wrapper => wrapper.slotTarget === slot);
    if (existingWrapper) {
      const knownArtworkById = new Map<string, any>();
      existingWrapper.artworks?.forEach((wrapperArtwork: any) => {
        if (wrapperArtwork?._id) knownArtworkById.set(String(wrapperArtwork._id), wrapperArtwork);
      });
      artworks?.forEach((knownArtwork: any) => {
        if (knownArtwork?._id) knownArtworkById.set(String(knownArtwork._id), knownArtwork);
      });
      knownArtworkById.set(String(artwork._id), artwork);

      existingWrapper.artworks = existingWrapper.artworks.map((wrapperArtwork: any, index: number) => {
        const wrapperArtworkId = String(wrapperArtwork?._id || wrapperArtwork);
        if (wrapperArtworkId === String(artwork._id)) {
          return artwork;
        }

        return knownArtworkById.get(wrapperArtworkId) || wrapperArtwork;
      });

      if (!existingWrapper.artworks[existingWrapper.activeArtwork]?._id) {
        const updatedArtworkIndex = existingWrapper.artworks.findIndex((wrapperArtwork: any) =>
          String(wrapperArtwork?._id || wrapperArtwork) === String(artwork._id)
        );
        if (updatedArtworkIndex >= 0) {
          existingWrapper.activeArtwork = updatedArtworkIndex;
        }
      }
    }

    if (selectedArtwork?._id === artwork?._id) {
      setSelectedArtwork(artwork);
    }
    setArtworks((previousArtworks) => previousArtworks.map((previousArtwork) =>
      String(previousArtwork?._id) === String(artwork?._id) ? artwork : previousArtwork
    ));
    main3DManager.loadArtwork(slot, existingWrapper, true);
  }

  const onArtworkWrapperChangedHandler = async (updatedWrapper: ArtworkWrapper | null | undefined) => {
    if (offlineModeRef.current) return;

    if (!updatedWrapper?._id || !appData.galleryData?.artworkWrappers) return;
    const galleryData = appData.galleryData;
    const wrapperIndex = galleryData.artworkWrappers.findIndex((wrapper) => wrapper?._id === updatedWrapper._id);
    if (wrapperIndex < 0) return;

    const existingWrapper = galleryData.artworkWrappers[wrapperIndex];
    const resolveIncomingWrapperArtworks = () => {
      const incomingArtworks = (updatedWrapper as any).artworks;
      if (!Array.isArray(incomingArtworks)) {
        return existingWrapper.artworks;
      }
      if (!incomingArtworks.length) {
        return [];
      }

      const knownArtworkById = new Map<string, any>();
      existingWrapper.artworks?.forEach((artwork: any) => {
        if (artwork?._id) knownArtworkById.set(String(artwork._id), artwork);
      });
      artworks?.forEach((artwork: any) => {
        if (artwork?._id) knownArtworkById.set(String(artwork._id), artwork);
      });
      incomingArtworks.forEach((artwork: any) => {
        if (artwork?._id) knownArtworkById.set(String(artwork._id), artwork);
      });

      return incomingArtworks.map((incomingArtwork: any) => {
        if (incomingArtwork && typeof incomingArtwork !== "string") {
          return incomingArtwork;
        }

        const incomingArtworkId = String(incomingArtwork || "");
        return knownArtworkById.get(incomingArtworkId) || incomingArtwork;
      });
    };
    const mergedWrapper = {
      ...existingWrapper,
      ...updatedWrapper,
      artworks: resolveIncomingWrapperArtworks(),
    } as ArtworkWrapper;
    const mergedArtworksById = new Map<string, Artwork>();
    mergedWrapper.artworks?.forEach((artwork: any) => {
      if (artwork?._id) {
        mergedArtworksById.set(String(artwork._id), artwork);
      }
    });

    galleryData.artworkWrappers[wrapperIndex] = mergedWrapper;
    const activeArtworkIndex = Number(mergedWrapper.activeArtwork);
    persistedPseudoMomActiveArtworkIndexesRef.current[String(mergedWrapper._id)] = Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0;
    appData.setGalleryData({ ...galleryData, artworkWrappers: [...galleryData.artworkWrappers] });
    setArtworks((previousArtworks) => previousArtworks.map((previousArtwork) =>
      mergedArtworksById.get(String(previousArtwork?._id)) || previousArtwork
    ));
    setSelectedArtwork(mergedWrapper.artworks?.[mergedWrapper.activeArtwork] || null);
    await main3DManager.loadArtwork(mergedWrapper.slotTarget, mergedWrapper, Boolean(mergedWrapper.artworks?.length));
    main3DManager.rendererService?.refreshPseudoMoMLightForSlot?.(mergedWrapper.slotTarget);
  }

  // create/edit artwork
  const editArtworkHandler = (artwork: Artwork) => {
    if (offlineModeRef.current) return;

    setArtworkToEdit(artwork);
    setArtworksModal(false);
    setCreateArtworkModal(true);
  }

  const createArtworkHandler = () => {
    if (offlineModeRef.current) return;

    setArtworkToEdit(undefined);
    setArtworksModal(false);
    setCreateArtworkModal(true);
  }

  const onCreatedArtworkHandler = async (edited: boolean, artworkPayload: Artwork | undefined) => {
    if (offlineModeRef.current) return;

    setCreateArtworkModal(false);

    if (!artworkPayload) {
      setArtworksModal(true);
      return;
    }

    /// check if edited is true, then update the artwork
    if (edited) {
      // replace selected artwork
      setSelectedArtwork(artworkPayload);

      setArtworks(artworks => artworks.map(a => a._id === artworkPayload._id ? artworkPayload : a));

      // if edited artwork, then check it's onsale or not
      if (artworkPayload.onsale) {
        // update wrapper
        const galleryData = appData.galleryData;
        let targetWrapper = null;
        for (const artworkWrapper of galleryData.artworkWrappers) {
          for (let j = 0; j < artworkWrapper.artworks.length; j++) {
            if (artworkWrapper.artworks[j]._id === artworkPayload._id) {
              artworkWrapper.artworks[j] = artworkPayload;
              targetWrapper = artworkWrapper;
              break;
            }
          }
        }

        main3DManager.loadArtwork(targetWrapper.slotTarget, targetWrapper, true);
      }
    } else {
      setArtworks(prev => [...prev, artworkPayload]);
    }

    setArtworksModal(true);
  }

  const showGallerySetupModal = () => {
    if (offlineModeState.isOffline) return;

    // disable keyboard event
    setGalleryKeyboardAvailability(false);
    setGallerySetupModal(true);
  }

  const moveArtwork = async () => {
    if (offlineModeState.isOffline) return;

    setMovingActionLoading(true);
    // const moveArtworks = document.getElementById("move-artworks");

    if (!isMovingArtworks) {
      // moveArtworks.innerText = "Save Artworks";
      main3DManager.selectAndMoveArtwork();

      // Initially reset the artwork type - don't set it yet
      setArtworkType("");

      // Set up event listener to detect artwork selection
      const checkSelectedArtwork = setInterval(() => {
        // Check if an artwork is selected in the transform controls
        const selectedObj = main3DManager.getSelectedArtwork();
        if (selectedObj) {
          // Access the artwork data through children nodes
          // Based on the structure observed in the event.service.ts file
          if (selectedObj.userData) {
            const artworkData = selectedObj.userData.artworkWrapper?.artworks?.[selectedObj.userData.artworkWrapper.activeArtwork];
            if (artworkData) {
              updateArtworkType(artworkData.type);
            } else {
              updateArtworkType("");
            }
          } else {
            updateArtworkType("");
          }
        } else {
          updateArtworkType("");
        }
      }, 200);
      // Store the interval ID in React ref
      checkIntervalRef.current = checkSelectedArtwork;
      setIsMovingArtworks(true);
      setMovingActionLoading(false);
    } else {
      try {
        const changedArtworks = main3DManager.getMovedArtworks()
        const artworksToMove = buildMovedArtworkPayloads(changedArtworks)

        const newWrappers = [];
        if (artworksToMove.length > 0) {
          const moveResponse = await apiClient.moveArtworks(id, artworksToMove);
          const { status } = moveResponse;
          if (status) {
            const returnedWrappers = moveResponse?.data?.wrappers;
            if (Array.isArray(returnedWrappers) && returnedWrappers.length > 0) {
              const hydrated = hydrateAutoPopulateArtworkWrappers(
                appData.galleryData.artworkWrappers,
                { wrappers: returnedWrappers },
                id || "",
                { preserveExistingArtworkObjects: true, preserveExistingActiveArtwork: true }
              );
              newWrappers.push(...hydrated.wrappersToLoad);
            } else {
              // update artwork wrapper to apply
              for (const artworkToMove of artworksToMove) {
                const wrapperToMove = appData.galleryData.artworkWrappers.find(w => w.slotTarget === artworkToMove.slotTarget);
                if (wrapperToMove) {
                  if (artworkToMove.artworkId) {
                    wrapperToMove.artworkTransforms = {
                      ...(wrapperToMove.artworkTransforms || {}),
                      [artworkToMove.artworkId]: {
                        position: artworkToMove.position,
                        rotation: artworkToMove.rotation,
                        scale: artworkToMove.scale,
                        ...(artworkToMove.pedestalDiameter !== undefined ? { pedestalDiameter: artworkToMove.pedestalDiameter } : {}),
                      },
                    };
                  } else {
                    wrapperToMove.transforms = {
                      position: artworkToMove.position,
                      rotation: artworkToMove.rotation,
                      scale: artworkToMove.scale
                    };
                  }

                  newWrappers.push(wrapperToMove);
                  // Update pedestal diameter if it was changed
                  if (artworkToMove.pedestalDiameter !== undefined) {
                    const activeArtwork = artworkToMove.artworkId
                      ? wrapperToMove.artworks.find((artwork: any) => String(artwork?._id || artwork) === String(artworkToMove.artworkId))
                      : wrapperToMove.artworks[wrapperToMove.activeArtwork];
                    if (activeArtwork?.artworkData?.pedestal) {
                      activeArtwork.artworkData.pedestal.diameter = artworkToMove.pedestalDiameter;
                    }
                  }
                }
              }
            }
            successNotify("Artworks have been moved");
          } else {
            failedNotify("Failed to move artworks")
          }
        }
        // moveArtworks.innerText = "Move Artworks";
        const octreeHelper = appData.spaceScene.getObjectByName("octree-mesh")
        // Use a for loop to iterate from the end to the start for safe removal
        for (let i = octreeHelper.children.length - 1; i >= 0; i--) {
          const child = octreeHelper.children[i];
          if (!child.name.startsWith("octree-mesh-")) {
            child.removeFromParent();
          }
        }
        // appData.physicsService.setCollision(octreeHelper)
        if (octreeHelper) {
          appData.physicsService.dispose()

          appData.physicsService.setCollision(octreeHelper)
        }

        for (const wrapper of newWrappers) {
          await main3DManager.loadArtwork(wrapper.slotTarget, wrapper, true);
        }

        // appData.physicsService.setCollision(appData.spaceScene.getObjectByName("octree-mesh"))
        // Clear the selection check interval when exiting move mode
        if (checkIntervalRef.current !== null) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }

        setArtworkType(""); // Reset artwork type when done moving
        setIsMovingArtworks(false);
      } finally {
        setMovingActionLoading(false);
      }
    }
  }

  const updateArtworkType = (type: string) => {
    setArtworkType(type);
  }

  const buildGallerySavePayload = useCallback((gallery: typeof appData.galleryData) => {
    return {
      _id: gallery._id,
      name: gallery.name,
      user: gallery.user,
      spaceId: gallery.spaceId,
      spaceType: gallery.spaceType,
      data: gallery.data,
      published: gallery.published
    };
  }, []);

  const applySpacePropVisibility = useCallback((props: Prop[], propIndex: number) => {
    const targetProp = props[propIndex];
    if (!targetProp) return;

    targetProp.meshNames.forEach((meshName) => {
      const sceneObject = appData.spaceScene?.getObjectByName(meshName);
      if (sceneObject) {
        sceneObject.visible = targetProp.visible;
      }
    });

    main3DManager?.rendererService?.addToRenderQueue();
  }, [main3DManager]);

  const onToggleSpaceProp = useCallback(async (propIndex: number) => {
    if (offlineModeRef.current) return;
    if (savingPropIndexes.includes(propIndex)) return;

    const currentSpaceData = appData.spaceData;
    const currentGalleryData = appData.galleryData;
    const currentProps = currentSpaceData?.data?.props || [];
    const targetProp = currentProps[propIndex];

    if (!currentSpaceData?.data || !currentGalleryData?._id || !targetProp) return;

    const updatedProps = currentProps.map((prop, index) => (
      index === propIndex
        ? { ...prop, visible: !prop.visible }
        : prop
    ));

    const updatedSpaceData = {
      ...currentSpaceData,
      data: {
        ...currentSpaceData.data,
        props: updatedProps
      }
    };

    setSavingPropIndexes((prev) => [...prev, propIndex]);
    appData.setSpaceData(updatedSpaceData);
    setSpaceProps(updatedProps);
    applySpacePropVisibility(updatedProps, propIndex);

    try {
      const response: any = await apiClient.updateGalleryBySeller(
        currentGalleryData._id,
        buildGallerySavePayload(currentGalleryData),
        updatedSpaceData
      );

      const saveSucceeded = typeof response?.status === "number"
        ? response.status === 200
        : Boolean(response && "data" in response ? response.data?.status : response?.status);
      if (!saveSucceeded) {
        throw new Error("Failed to save prop visibility");
      }
    } catch (error) {
      appData.setSpaceData(currentSpaceData);
      setSpaceProps(currentProps);
      applySpacePropVisibility(currentProps, propIndex);
      failedNotify("Failed to save prop visibility");
    } finally {
      setSavingPropIndexes((prev) => prev.filter((index) => index !== propIndex));
    }
  }, [apiClient, applySpacePropVisibility, buildGallerySavePayload, failedNotify, savingPropIndexes]);

  const onChangeMood = async (newMood: any, local?: any) => {
    const parsedMoodIndex =
      typeof newMood === "number"
        ? newMood
        : Number.parseInt(String(newMood), 10);

    if (Number.isInteger(parsedMoodIndex) && parsedMoodIndex >= 0) {
      if (!appData.galleryData.data) {
        appData.galleryData.data = {} as any;
      }
      appData.galleryData.data.mood = parsedMoodIndex;
      appData.setGalleryData(appData.galleryData);
    }

    if (local) {
      await main3DManager.changeMood(parsedMoodIndex, local);
      return;
    }

    const selectedMoodData =
      Number.isInteger(parsedMoodIndex) && parsedMoodIndex >= 0
        ? appData.spaceData?.data?.moods?.[parsedMoodIndex]
        : undefined;
    await main3DManager.changeMood(parsedMoodIndex, selectedMoodData);
  }

  const onSelectMoodForGallery = async (moodID: number): Promise<boolean> => {
    if (offlineModeRef.current) return false;

    const galleryId = appData.galleryData?._id;
    if (!galleryId) return false;

    const resp = await apiClient.changeMoodForGallery(galleryId, moodID);
    const persisted = Boolean(resp?.status);
    if (!persisted) return false;

    if (!appData.galleryData.data) {
      appData.galleryData.data = {} as any;
    }
    appData.galleryData.data.mood = moodID;
    appData.setGalleryData(appData.galleryData);
    return true;
  }

  const onPlaying = (playing: boolean) => {
    // TODO: sounds can only be played upon user interaction
    // if (playing) {
    //   main3DManager.soundService.pauseSounds();
    // } else {
    //   main3DManager.soundService.playSounds();
    // }
  }

  const onMute = async (muted: boolean) => {
    if (muted || visitorBackgroundMusicMuted) {
      main3DManager.soundService.mute();
    } else {
      await main3DManager.soundService.unmute();
    }
    if (appData.galleryData?.data?.sound) {
      appData.galleryData.data.sound.muted = muted;
      appData.setGalleryData(appData.galleryData);
    }
    setHasPlayableGalleryBackgroundMusic(
      isGalleryBackgroundMusicPlayable(appData.galleryData?.data?.sound)
    );
  }

  const handleSoundChanged = async (sound?: Sounds) => {
    if (offlineModeRef.current) return;

    if (!sound) {
      setHasPlayableGalleryBackgroundMusic(false);
      return;
    }

    appData.setGalleryData({
      ...appData.galleryData,
      data: {
        ...appData.galleryData.data,
        sound: {
          ...appData.galleryData.data.sound,
          soundId: sound._id,
          url: sound.url,
          soundVolume: (appData.galleryData.data.sound as any)?.soundVolume ?? 1,
        }
      }
    });
    setIsBackgroundSoundLoading(true);
    await main3DManager.loadBackgroundSound();
    setIsBackgroundSoundLoading(false);
    setHasPlayableGalleryBackgroundMusic(
      isGalleryBackgroundMusicPlayable(appData.galleryData?.data?.sound)
    );
  }

  const onAutoPopulate = async (data: any) => {
    if (offlineModeRef.current) return;

    if (data.status) {
      setGalleryStats(normalizeGalleryStats(data.stats, appData.galleryData?.artworkLoadQuotaMb));
      const l = `Artworks listed: ${data.listed}`;
      const n = `Artworks not listed: ${data.notListed}`
      successNotify(<div>{l}<br />{n}</div>);
      const skippedArtworkMessage = buildSkippedGalleryQuotaMessage(data.skippedArtworks)
        || buildSkippedArtworkMessage(data.skippedArtworks);
      if (skippedArtworkMessage) {
        failedNotify(skippedArtworkMessage);
      }

      // add listed artworks to gallery data

      const hydration = hydrateAutoPopulateArtworkWrappers(
        appData.galleryData.artworkWrappers,
        data,
        appData.galleryData._id
      );
      const artworkWrappers = hydration.artworkWrappers;
      const newWrappers = hydration.wrappersToLoad;

      if (newWrappers.length === 0 && !(data.createdArtworks && data.createdArtworks.length > 0)) {
        (data.slotTargets || []).forEach((st: { id: string, slotTarget: number }) => {
          setArtworks(artworks.map(a => {
            if (a._id === st.id) {
              a.onsale = true;

              // add to artworksData
              artworkWrappers.push({
                _id: a._id,
                // name: a.title,
                // owner: "",
                galleryBelongsTo: appData.galleryData._id,
                slotTarget: st.slotTarget,
                // transforms: a.transforms,
                artworks: [a],
                activeArtwork: 0,
                transforms: {
                  position: {
                    x: 0,
                    y: 0,
                    z: 0
                  },
                  rotation: {
                    x: 0,
                    y: 0,
                    z: 0
                  },
                  scale: {
                    x: 1,
                    y: 1,
                    z: 1
                  }
                }
              })
            }
            return a;
          }))
        })
      }

      appData.galleryData.artworkWrappers = artworkWrappers;
      for (const wrapper of newWrappers) {
        await main3DManager.loadArtwork(wrapper.slotTarget, wrapper, true);
      }

      /// close modal
      setArtworksModal(false);
    } else {
      failedNotify(data.msg);
    }
  }




  const onEnquireHandler = () => {
    if (offlineModeState.isOffline) return;
    // hide buy modal???
    setEnquireModal(true);
  }

  const onAddArtworkToPseudoMoMPillar = () => {
    if (offlineModeState.isOffline) return;
    if (!canUsePseudoMoMPillarActionsRef.current) return;
    setBuyArtworkModal(false);
    setSelectedArtwork(null);
    setArtworksModal(true);
    setGalleryKeyboardAvailability(false);
  }

  const onRemoveArtworkFromPseudoMoMPillar = async () => {
    if (offlineModeState.isOffline) return;
    if (!canUsePseudoMoMPillarActionsRef.current || !selectedArtwork?._id) return;

    const selectedWrapper = appData.galleryData?.artworkWrappers?.find((wrapper) =>
      Number(wrapper?.slotTarget) === Number(selectedSlot)
    );
    if (!selectedWrapper || !wrapperContainsArtwork(selectedWrapper, selectedArtwork._id)) {
      failedNotify("Failed to remove artwork from pillar");
      return;
    }

    const slotTarget = Number(selectedWrapper.slotTarget);
    const data = await apiClient.putArtworkOnSale(selectedArtwork._id, id, slotTarget, false);
    if (data?.status) {
      setBuyArtworkModal(false);
      const didApply = await onListingArtworkHandler({
        ...data,
        listing: false,
        slotTarget
      }, data.stats);
      if (!didApply) return;
      successNotify("Artwork removed from pillar");
    } else {
      failedNotify(data?.msg || "Failed to remove artwork from pillar");
    }
  }

  const onSentEnquire = (status: boolean, msg?: string) => {
    if (status) {
      successNotify("Enquiry sent");
      setEnquireModal(false);
    } else {
      failedNotify(msg);
    }
  }

  const onApplyMood = async (moodID: number, data: any) => {
    if (offlineModeRef.current) return;

    const galleryData = appData.galleryData;
    const response = await apiClient.upsertMood(galleryData.spaceId, data, moodID);

    if (response?.status) {
      const persisted = resolveSavedMoodPersistence(
        appData.spaceData?.data?.moods,
        moodID,
        data,
        response
      );
      const spaceData = {
        ...appData.spaceData,
        data: {
          ...appData.spaceData.data,
          moods: persisted.moods,
        },
      };
      appData.setSpaceData(spaceData);
      setMoods(persisted.moods);

      let moodSelectionPersisted = true;
      if (galleryData?._id) {
        const moodSelectionResp = await apiClient.changeMoodForGallery(galleryData._id, persisted.moodIndex);
        moodSelectionPersisted = Boolean(moodSelectionResp?.status);
      }

      if (moodSelectionPersisted) {
        if (!galleryData.data) {
          galleryData.data = {} as any;
        }
        galleryData.data.mood = persisted.moodIndex;
        appData.setGalleryData(galleryData);
        successNotify("Mood has been modified");
      } else {
        failedNotify("Mood settings were saved, but failed to set the selected mood for this gallery.");
      }
    } else {
      failedNotify("Failed to save mood setting.");
    }
  }

  const onQualityChanged = (quality: "LQ" | "MQ" | "HQ") => {
    localStorage.setItem('renderQuality', quality);
    window.location.reload();
  }

  const quotaStats = galleryStats.artworkLoadQuota;
  const quotaUsagePercent = getGalleryQuotaUsagePercent(quotaStats);
  const hasUnknownQuotaSizes = quotaStats.unknownArtworkIds.length > 0;
  const shouldShowQuotaStats = showGalleryStatsPanel && quotaStats.enabled !== false;
  const galleryStatsPanelBottomPx = isTouchDevice ? 170 : 20;
  const galleryStatsPanelEstimatedHeightPx = shouldShowQuotaStats ? 180 : 120;
  const spacePropsPanelBottomPx = showGalleryStatsPanel
    ? galleryStatsPanelBottomPx + galleryStatsPanelEstimatedHeightPx + 12
    : (isTouchDevice ? 350 : 110);
  const currentWrapperForModal = appData.galleryData?.artworkWrappers?.find((wrapper) => Number(wrapper?.slotTarget) === Number(selectedSlot));
  const canMoveArtworks = resolveCanMoveArtworks(currentRouteMode, {
    admin,
    personal,
    isAdminAsSeller,
  });
  const persistedActiveArtworkIndexForModal = currentWrapperForModal?._id
    ? persistedPseudoMomActiveArtworkIndexesRef.current[String(currentWrapperForModal._id)]
    : undefined;
  const offlineCachedHref = offlineModeState.cachedGallery?.routePath;
  const openSavedOfflineGallery = useCallback(() => {
    if (!offlineCachedHref) return;
    try {
      window.history.replaceState(null, "", offlineCachedHref);
      window.location.reload();
    } catch (_error) {
      window.location.assign(offlineCachedHref);
    }
  }, [offlineCachedHref]);

  return (
    <div className="position-relative">
      <div id="WebGL" draggable="false" ref={canvasRef}>
      </div>
      {/* <div id="prompt-text"></div> */}
      <div id="loading" className="loading-screen">Loading ...</div>
      {offlineModeState.isOffline && offlineModeState.status === "active" && (
        <div className="offline-gallery-banner" role="status">
          Offline copy
        </div>
      )}
      {offlineModeState.status === "unavailable" && (
        <div className="offline-gallery-unavailable" role="alert">
          <div className="offline-gallery-unavailable-title">Gallery unavailable offline</div>
          <div className="offline-gallery-unavailable-message">{offlineModeState.message}</div>
          {offlineCachedHref ? (
            <button
              type="button"
              className="offline-gallery-unavailable-action"
              onClick={openSavedOfflineGallery}
            >
              Open saved gallery
            </button>
          ) : null}
        </div>
      )}
      <div id="joystick-background"></div>
      <div id="joystick-thumb"></div>
      {!offlineModeState.isOffline && (personal || canOpenGallerySetup) && !gallerySetupModal &&
        <div className={`sidebar-left ${admin ? "sidebar-left-admin" : ""} position-absolute`} style={{ top: admin ? "180px" : "150px", left: "20px" }}>
          <div className="center-buttons">
            {canOpenGallerySetup && <button className="gallery-btn btn-secondary" onClick={showGallerySetupModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Gallery Setup
            </button>}
            {canMoveArtworks && <button
              id="move-artworks"
              className="gallery-btn btn-secondary"
              onClick={moveArtwork}
              disabled={movingActionLoading}
              aria-busy={movingActionLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              {movingActionLoading
                ? (isMovingArtworks ? 'Saving...' : 'Enabling...')
                : (isMovingArtworks ? 'Save Artworks' : 'Move Artworks')}
            </button>}
          </div>
        </div>
      }

      {!offlineModeState.isOffline && personal && spaceProps.length > 0 && (
        <div className="position-fixed stats-panel" style={{ bottom: `${spacePropsPanelBottomPx}px`, zIndex: 11 }}>
          {spaceProps.map((prop, index) => (
            <div
              key={`space-prop-toggle-${index}`}
              className="stat-row"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
            >
              <span className="stat-label">{`Toggle on/off prop ${index + 1}`}</span>
              <div className="form-check form-switch" style={{ margin: 0, minHeight: "auto", paddingLeft: "2.5em" }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`space-prop-toggle-${index}`}
                  checked={prop.visible}
                  disabled={savingPropIndexes.includes(index)}
                  aria-busy={savingPropIndexes.includes(index)}
                  style={{
                    cursor: savingPropIndexes.includes(index) ? "wait" : "pointer",
                    width: "2em",
                    height: "1em",
                    marginTop: "0.25em",
                    opacity: savingPropIndexes.includes(index) ? 0.7 : 1
                  }}
                  onChange={() => onToggleSpaceProp(index)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {showGalleryStatsPanel && <div className="position-fixed stats-panel" style={{ bottom: `${galleryStatsPanelBottomPx}px` }}>
        <div className="stat-row"><span className="stat-label">Open hexagons</span> <span className="stat-value">{galleryStats.openHexagonsCount}</span></div>
        <div className="stat-row"><span className="stat-label">Listed artworks</span> <span className="stat-value">{galleryStats.totalListedCount}</span></div>
        {shouldShowQuotaStats && <>
          <div className="stat-row"><span className="stat-label">Display load</span> <span className="stat-value">{formatQuotaMb(quotaStats.currentMb)} / {formatQuotaMb(quotaStats.quotaMb)} MB</span></div>
          <div
            className="quota-meter"
            aria-label={`Gallery display load ${formatQuotaMb(quotaStats.currentMb)} of ${formatQuotaMb(quotaStats.quotaMb)} MB`}
          >
            <div
              className={`quota-meter-fill${quotaUsagePercent >= 90 ? " quota-meter-fill-warning" : ""}`}
              style={{ width: `${quotaUsagePercent}%` }}
            />
          </div>
          <div className="stat-row quota-left-row">
            <span className="stat-label">Load left</span>
            <span className="stat-value">{hasUnknownQuotaSizes ? "Backfill needed" : `${formatQuotaMb(quotaStats.remainingMb)} MB`}</span>
          </div>
        </>}
        <div className="stat-row"><span className="stat-label">Free placeholders</span> <span className="stat-value">{galleryStats.availableSlotsCount}</span></div>
      </div>}

      {!offlineModeState.isOffline && canMoveArtworks && isMovingArtworks && (
        <div className="position-absolute" style={{
          top: "220px",
          left: "25px",
          color: "white",
          textShadow: "rgb(0, 0, 0) -1px -1px 0px, rgb(0, 0, 0) 1px -1px 0px, rgb(0, 0, 0) -1px 1px 0px, rgb(0, 0, 0) 1px 1px 0px",
          backgroundColor: "#374151",
          padding: "8px 20px",
          fontWeight: "normal",
          paddingTop: "9px",
          height: "45px",
          borderRadius: "25px",
        }}>
          Press Z to Move, and X to Rotate the object
        </div>
      )}
      {!offlineModeState.isOffline && canMoveArtworks && isMovingArtworks && (artworkType === 'image' || artworkType === 'video' || artworkType === 'sound') && (
        <div className="position-absolute" style={{
          top: "280px",
          left: "25px",
          color: "white",
          textShadow: "rgb(0, 0, 0) -1px -1px 0px, rgb(0, 0, 0) 1px -1px 0px, rgb(0, 0, 0) -1px 1px 0px, rgb(0, 0, 0) 1px 1px 0px",
          backgroundColor: "#374151",
          padding: "8px 20px",
          fontWeight: "normal",
          height: "45px",
          borderRadius: "25px",
          display: "flex",
          alignItems: "center"
        }}>
          <div className="form-check form-switch" style={{ margin: 0, paddingLeft: "2.5em" }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="artwork-toggle"
              checked={isToggleOn}
              style={{ cursor: "pointer", width: "2em", height: "1em", marginTop: "0.25em" }}
              onChange={(e) => {
                const newState = e.target.checked;
                setIsToggleOn(newState);
                localStorage.setItem("snapToWall", String(newState));
              }}
            />
            <label className="form-check-label" htmlFor="artwork-toggle" style={{ cursor: "pointer", marginLeft: "10px" }}>
              Snap to Wall
            </label>
          </div>
        </div>
      )}
      {!offlineModeState.isOffline && admin && !renderSettingsModal && <button className="btn-custom position-absolute" style={{ top: "100px", left: "20px" }} onClick={() => {
        setRenderSettingsModal(true);
      }}>
        Render Settings
      </button>}
      {!offlineModeState.isOffline && canEditArtworkSlots && artworksModal && <ListArtworkModalV2
        show={artworksModal}
        onHide={
          () => {
            appData.renderService.addToRenderQueue();
            setArtworksModal(false);
          }
        }
        apiClient={apiClient}
        slot={Number(selectedSlot)}
        artworks={artworks}
        currentSelected={selectedArtwork}
        galleryId={id}
        onArtworkChangedHandler={onArtworkChangedHandler}
        onListingHandler={onListingArtworkHandler}
        createArtworkHandler={createArtworkHandler}
        editArtworkHandler={editArtworkHandler}
        onAutoPopulate={onAutoPopulate}
        forcedExternalSources={forcedExternalSources}
        includeEuropeanaSounds={canUsePseudoMoMPillarActionsRef.current}
        canUseArtworkRepositoryActions={canUseArtworkRepositoryActions}
        canManagePseudoMoMPillars={canUsePseudoMoMPillarActionsRef.current}
        currentWrapper={currentWrapperForModal}
        persistedActiveArtworkIndex={persistedActiveArtworkIndexForModal}
        onArtworkWrapperChanged={onArtworkWrapperChangedHandler}
        spaceData={appData.spaceData}
        onPauseBackgroundSound={() => main3DManager?.soundService?.pauseBackgroundSound()}
        onResumeBackgroundSound={() => main3DManager?.soundService?.playBackgroundSound()}
      />}
      {
        buyArtworkModal && selectedArtwork && <BuyArtworkModal
          onBuyHandler={() => { }}
          artwork={selectedArtwork}
          onHide={() => {
            setBuyArtworkModal(false);
          }}
          galleryId={id}
          show={buyArtworkModal}
          slot={Number(selectedSlot)}
          onEnquireHandler={onEnquireHandler}
          successNotify={successNotify}
          failedNotify={failedNotify}
          offlineMode={offlineModeState.isOffline}
          canAddArtworkToSlot={!offlineModeState.isOffline && canUsePseudoMoMPillarActionsRef.current}
          onAddArtworkToSlot={onAddArtworkToPseudoMoMPillar}
          canRemoveArtworkFromSlot={!offlineModeState.isOffline && canUsePseudoMoMPillarActionsRef.current}
          onRemoveArtworkFromSlot={onRemoveArtworkFromPseudoMoMPillar}
          isPseudoMoMArtwork={isPseudoMoMSpace(appData.spaceData)}
        />
      }
      {
        !personal && !offlineModeState.isOffline && enquireModal && <EnquireModal
          show={enquireModal}
          onHide={() => setEnquireModal(false)}
          artwork={selectedArtwork}
          onSent={onSentEnquire}
        />
      }
      {
        !offlineModeState.isOffline && personal && createArtworkModal && <NewArtworkModal
          onCreatedHandler={onCreatedArtworkHandler}
          show={createArtworkModal}
          artworkToEdit={artworkToEdit}
          onSuccess={successNotify}
          onFailed={failedNotify}
        />
      }
      {
        !offlineModeState.isOffline && personal && confirmDeleteHexagon && <ConfirmDeleteHexagonModal
          onHide={() => setConfirmDeleteHexagon(false)}
          show={confirmDeleteHexagon}
          onRemove={onConfirmRemoveHexagon}
          space={spaceToRemove}
        />
      }
      {
        !offlineModeState.isOffline && personal && confirmOpenHexagon && <ConfirmOpenHexagonModal
          onHide={() => setConfirmOpenHexagon(false)}
          show={confirmOpenHexagon}
          onOpen={onOpenHexagon}
          space={spaceToOpen}
        />
      }
      {!offlineModeState.isOffline && <GallerySetupModal
        personal={personal}
        canUseManagementSections={canUseGallerySetupManagementSections}
        canManageCollaborators={canManageGalleryCollaborators}
        canManageGallery={canManageGallery}
        galleryOwnerId={appData.galleryData?.user}
        collaborators={collaborators}
        onCollaboratorsUpdated={async () => {
          const resp = await apiClient.getGalleryById(id);
          if (resp?.status && resp.gallery) {
            appData.setGalleryData({ ...appData.galleryData, ...resp.gallery, collaborators: resp.gallery.collaborators });
            setCollaborators(resp.gallery.collaborators || []);
          }
        }}
        onHide={() => {
          setGallerySetupModal(false);
        }}
        show={gallerySetupModal}
        isBackgroundSoundLoading={isBackgroundSoundLoading}
        galleryId={appData.galleryData?._id || id}
        includeUnpublishedMoods={admin || isAdminAsSeller}
        published={published}
        onChangePublished={setPublished}
        onPlaying={onPlaying}
        onMute={onMute}
        moods={moods}
        currentTitle={title}
        onChangeTitle={setTitle}
        onChangeMood={onChangeMood}
        currentMood={appData.galleryData?.data?.mood || 0}
        currentSoundId={appData.galleryData?.data?.sound?.soundId}
        currentMuted={appData.galleryData?.data?.sound?.muted}
        onSoundChanged={handleSoundChanged}
        onPauseBackgroundSound={() => main3DManager?.soundService?.pauseBackgroundSound()}
        onResumeBackgroundSound={() => main3DManager?.soundService?.playBackgroundSound()}
        selectedLabelIds={extractGalleryLabelIds(appData.galleryData)}
        onGalleryLabelsSaved={({ labelIds, labels }) => {
          appData.setGalleryData({
            ...appData.galleryData,
            labelIds,
            labels,
          });
        }}
        currentPseudoMomIntroDurationSeconds={resolveGalleryIntroDurationSeconds(
          appData.spaceData,
          appData.galleryData
        )}
        currentPseudoMomIntroSoundId={appData.galleryData?.data?.pseudoMomIntroSound?.soundId ?? null}
        currentPseudoMomAutoRotateObjects={appData.galleryData?.data?.pseudoMomAutoRotateObjects === true}
        currentPseudoMomManualRotateObjects={appData.galleryData?.data?.pseudoMomManualRotateObjects === true}
        onPseudoMomIntroSettingsChanged={({ durationSeconds, introSound, autoRotateObjects, manualRotateObjects }) => {
          appData.setGalleryData({
            ...appData.galleryData,
            data: {
              ...(appData.galleryData?.data || {}),
              pseudoMomIntroDurationSeconds: durationSeconds,
              pseudoMomIntroSound: introSound,
              pseudoMomAutoRotateObjects: autoRotateObjects,
              pseudoMomManualRotateObjects: manualRotateObjects,
            } as any,
          });
          main3DManager.setViewerRotationEnabled(
            ENABLE_3D_ARTWORK_DRAG_ROTATION &&
            manualRotateObjects &&
            currentRouteMode !== "personal" &&
            currentRouteMode !== "admin"
          );
        }}
      />}
      {!offlineModeState.isOffline && admin && renderSettingsModal && <RenderSettingsModal
        show={renderSettingsModal}
        onHide={() => {
          setRenderSettingsModal(false);
        }}
        currentMood={moods[appData.galleryData.data.mood]}
        onChangeMood={onChangeMood}
        onSelectMoodForGallery={onSelectMoodForGallery}
        onApplyMood={onApplyMood}
        appData={appData}
        onGetFilesFromFolder={getFilesFromFolder}
        onUploadFilesToFolder={uploadFilesToFolder}
        failedNotify={failedNotify}
      />}
      <ToastContainer />

    </div>
  );
});
